import React, { useState, useRef, useEffect, useMemo } from "react";
import LeftSidebar from "../../components/LeftSidebar";
import { backgroundColor, iconsColor, iconsTemplate } from "../../core/utils";
import { Dialog } from "primereact/dialog";
import DesignsUpload from "../../components/DesignsUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Toast } from "primereact/toast";
import { toastAlert } from "../../core/utils";
import ExpandSidebar from "../../components/ExpandSidebar";
import ButtonsActions from "../../components/ButtonsActions";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { AssignmentService } from "../../service/AssignmentService";
import { useAuth } from "../../hooks/useAuth";
import { faCheckDouble, faSyncAlt, faCheck } from "@fortawesome/free-solid-svg-icons";

function DesignUpload({ visible, hide, items, updateData, updateNotify }) {
    const service = new AssignmentService();
    const { 0: auth } = useAuth();
    const toast = useRef(null);
    const [localItems, setLocalItems] = useState(items);

    const [modalState, setModalState] = useState({
        upload: { visible: false, title: "", format: null },
        notify: { visible: false, loading: false },
        fullscreen: { visible: false, data: null },
    });

    const [updateTrigger, setUpdateTrigger] = useState(0);

    const forceUpdate = () => {
        setUpdateTrigger((prev) => prev + 1); // Función para forzar actualización
    };

    const [formData, setFormData] = useState({
        textNotify: "",
        currentItem: null,
        currentSocial: null,
        currentFormat: null,
        active: true,
    });

    useEffect(() => {
        verifyUploaded(items);
        setLocalItems(items);
    }, [items]);

    const handleMouseInteraction = (e, social, action) => {
        const color = backgroundColor(social);
        e.target.classList[action](color);
    };

    const handleSelection = (id, items, social, dimension) => {
        const formatKey = `${social} ${dimension}`;
        const latestUpload = getLatestUpload(items, formatKey);
        const lastStatus = latestUpload?._FILESTATUS?.slice(-1)[0];

        if (!latestUpload || lastStatus === "DISAPPROVE") {
            openUploadModal(items, social, dimension, latestUpload);
        } else {
            openFullscreenModal(id, [latestUpload], social, dimension);
        }
    };

    const getLatestUpload = (items, formatKey) => {
        return (items._UPLOADED || []).filter((ft) => ft._FILEFORMAT === formatKey).sort((a, b) => new Date(b._FILEDATE) - new Date(a._FILEDATE))[0];
    };

    const verifyUploaded = (currentItems) => {
        if (currentItems) {
            if (!currentItems._FORMATS || !currentItems._UPLOADED) return;

            const totalFormats = Object.values(currentItems._FORMATS).reduce((sum, format) => sum + format.socialFormat.length, 0);

            setFormData((prev) => ({
                ...prev,
                active: totalFormats !== currentItems._UPLOADED.length,
            }));
        }
    };

    // Funciones para manejar modales
    const openUploadModal = (data, social, format, fileToEdit = null) => {
        setModalState((prev) => ({
            ...prev,
            upload: {
                visible: true,
                title: `${fileToEdit ? "Update" : "Upload"} ${social} ${format}`,
                currentSocialFormat: `${social} ${format}`,
                format,
                fileToEdit,
            },
        }));
        setFormData((prev) => ({
            ...prev,
            currentItem: data,
            currentSocial: social,
            currentFormat: format,
        }));
    };

    const openFullscreenModal = (id, item, social, dimension) => {
        setModalState((prev) => ({
            ...prev,
            fullscreen: {
                visible: true,
                data: { id, item, social, dimension },
            },
        }));
    };

    const closeModal = (modalName) => {
        setModalState((prev) => ({
            ...prev,
            [modalName]: { ...prev[modalName], visible: false },
        }));
    };

    const handleNotifySubmit = async () => {

        setModalState((prev) => ({
            ...prev,
            notify: { ...prev.notify, loading: true },
        }));

        try {

            if (!service || !service.notifyDesigns) {
                throw new Error("Service not initialized");
            }

            const payload = {
                _ID: items._IDASSIGNMET,
                _DESIGNER: items._DESIGNER,
                _OBSERVATION: formData.textNotify || "",
            };

            //console.log("Enviando datos:", payload); // Debug

            const response = await service.notifyDesigns(payload);

           // console.log("Respuesta recibida:", response);

            if (response._SUCCESS) {
                updateNotify(response._ITEMS);
                setFormData((prev) => ({ ...prev, textNotify: "" }));
                closeModal("notify");
                hide();
                toastAlert("success", response._MESSAGE, toast);
            } else {
                toastAlert("warn", response._MESSAGE || "Error desconocido", toast);
            }
        } catch (error) {
            console.error("Error en notificación:", error);
            toastAlert("error", error.response?.data?.message || error.message || "Error al notificar diseños", toast);
        } finally {
            setModalState((prev) => ({
                ...prev,
                notify: { ...prev.notify, loading: false },
            }));
        }
    };

    const updateDatas = (updatedItem) => {
        setLocalItems(updatedItem);
        verifyUploaded(updatedItem);
        updateData(updatedItem);
        forceUpdate();
    };

    const renderStatusIcon = (status) => {
        if (!status) {
            return <i className="pi pi-upload mr-2 text-xl text-yellow-600"></i>;
        }

        switch (status.toString().toUpperCase()) {
            case "DISAPPROVE":
                return <FontAwesomeIcon icon={faSyncAlt} className="mr-2 text-xl text-blue-500" />;
            case "SAVED":
                return <FontAwesomeIcon icon={faCheck} className="mr-2 text-xl text-green-600" />;
            case "APPROVED":
                return <FontAwesomeIcon icon={faCheckDouble} className="mr-2 text-xl text-green-600" />;
            default:
                return <i className="pi pi-upload mr-2 text-xl text-yellow-600"></i>;
        }
    };

    const socialMediaElements = useMemo(() => {
        if (!localItems._FORMATS) return null;

        return Object.values(localItems._FORMATS).map((format, i) => {
            const social = format.socialMedia;
            const icon = iconsTemplate(social);
            const socialName = social.toUpperCase();

            return (
                <div className="text-900 w-full md:flex-order-0 flex-order-1 align-items-center" key={i}>
                    {format.socialFormat.map((dimension, x) => {
                        const latestUpload = (localItems._UPLOADED || []).filter((ft) => ft._FILEFORMAT === `${social} ${dimension}`).sort((a, b) => new Date(b._FILEDATE) - new Date(a._FILEDATE))[0];

                        const fileStatus = latestUpload?._FILESTATUS?.slice(-1)[0];

                        return (
                            <div
                                key={`${i}-${x}`}
                                className="border-1 p-4 mb-2 border-round flex flex-column md:flex-row align-items-start md:align-items-center md:justify-content-between cursor-pointer surface-card surface-border"
                                onMouseEnter={(e) => handleMouseInteraction(e, social, "add")}
                                onMouseLeave={(e) => handleMouseInteraction(e, social, "remove")}
                                onClick={() => handleSelection(localItems._IDASSIGNMET, localItems, social, dimension)}
                            >
                                <div className="flex align-items-start">
                                    {social === "TIKTOK" ? <FontAwesomeIcon icon={["fab", "tiktok"]} className="mr-2" style={{ fontSize: "2em", color: iconsColor(social) }} /> : <i className={`mr-2 pi ${icon}`} style={{ fontSize: "2em", color: iconsColor(social) }}></i>}
                                    <div>
                                        <div className="font-medium mb-2 text-900">{socialName}</div>
                                        <span className="p-1 border-round text-600">{dimension}</span>
                                    </div>
                                </div>
                                <div className="ml-0 md:ml-8 mt-2 md:mt-0 p-1 px-2 flex align-items-center text-600">{renderStatusIcon(fileStatus)}</div>
                            </div>
                        );
                    })}
                </div>
            );
        });
    }, [localItems, updateTrigger]);

    const hasPermission = [items._IDDESIGNER === auth._ID, auth._ROLNAME === "Design Supervisor", auth._ROLNAME === "Supervisor", auth._ROLNAME === "Administrator"].some(Boolean);

    return (
        <div>
            <Toast ref={toast} style={{ fontSize: "14px" }} />

            <LeftSidebar onVisible={visible} onHide={hide} onWidth={450}>
                <h2 className="text-color">Design Upload</h2>
                <div className="h-full">{socialMediaElements}</div>

                {/* Modal de Upload/Update */}
                <Dialog visible={modalState.upload.visible} style={{ width: "550px" }} header={`Design ${modalState.upload.title}`} modal onHide={() => closeModal("upload")}>
                    <DesignsUpload data={formData.currentItem} format={modalState.upload.currentSocialFormat} alert={toastAlert} updateData={updateDatas} hide={() => closeModal("upload")} uploadNotify={verifyUploaded} editData={modalState.upload.fileToEdit} />
                </Dialog>

                {/* Modal de Notificación */}
                <Dialog
                    visible={modalState.notify.visible}
                    style={{ width: "550px" }}
                    header="Notify Design Upload"
                    modal
                    onHide={() => closeModal("notify")}
                    footer={
                        <>
                            <Button label="No" disabled={modalState.notify.loading} icon="pi pi-times" className="p-button-text" onClick={() => closeModal("notify")} />
                            <Button label="Yes" loading={modalState.notify.loading} icon="pi pi-check" className="p-button-text" onClick={handleNotifySubmit} />
                        </>
                    }
                >
                    <div className="flex align-items-center justify-content-center">
                        <div className="field col-12">
                            <label htmlFor="observation" className="font-semibold">
                                Observation:
                            </label>
                            <InputTextarea
                                rows={2}
                                cols={50}
                                value={formData.textNotify}
                                onChange={(e) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        textNotify: e.target.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </Dialog>

                {hasPermission && (
                    <ButtonsActions
                        cancel={hide}
                        text="Notify"
                        submit={() =>
                            setModalState((prev) => ({
                                ...prev,
                                notify: { ...prev.notify, visible: true },
                            }))
                        }
                        isNotify={formData.active}
                    />
                )}
            </LeftSidebar>

            {/* Modal Fullscreen */}
            <ExpandSidebar
                visible={modalState.fullscreen.visible}
                id={modalState.fullscreen.data?.id}
                hide={() => closeModal("fullscreen")}
                items={modalState.fullscreen.data?.item || []}
                social={modalState.fullscreen.data?.social}
                format={modalState.fullscreen.data?.dimension}
                updateItems={updateDatas}
                updateButton={verifyUploaded}
            />
        </div>
    );
}

export default DesignUpload;
